import { ChangeDetectionStrategy, Component, DestroyRef, Input, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CACHE_NAMES,
  DEFAULT_GROUP_ICON,
  DEFAULT_PROFILE_ICON,
  PRESENCE_STATUS_CLASSES,
  PresenceStatus,
} from '../../enum/constants';
import { getColorByLetter, getInitials } from '../../helpers';
import { AvatarService } from '../../services/avatar.service';
import { CacheService } from '../../services/cache.service';
import { CrmService } from '../../services/crm.service';

/** Anything that isn't already a loadable URL is a files-service key, e.g. `avatars/<uuid>.jpg` */
const NON_KEY_URL = /^(https?:|blob:|data:|\/|assets\/)/;
/** Remote images go through the avatar cache; local ones (blob:, data:, assets) are used as is */
const REMOTE_URL = /^https?:/;

@Component({
  selector: 'app-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AvatarComponent implements OnInit {
  private readonly avatarService = inject(AvatarService);
  private readonly cacheService = inject(CacheService);
  protected readonly crmService = inject(CrmService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() chatId?: string;
  @Input() mediaId?: string | number;
  /** Full URL or a files-service key (`avatars/<uuid>.jpg`), so callers can pass `avatar_url` as is */
  @Input() set url(value: string | null | undefined) {
    const source =
      value && !NON_KEY_URL.test(value) ? `${environment.filesURL}/files/download?key=${value}` : value || undefined;
    this.hasImageError = false;
    this.imageSubscription?.unsubscribe();

    if (!source || !REMOTE_URL.test(source)) {
      this._url = source;
      return;
    }

    this._url = undefined;
    this.imageSubscription = this.cacheService
      .getCachedObjectUrl(source, CACHE_NAMES.AVATAR_IMAGE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cachedUrl) => (this._url = cachedUrl));
  }
  get url(): string | undefined {
    return this._url;
  }
  private _url?: string;
  private imageSubscription?: Subscription;
  @Input() additionalUrl?: string;
  @Input() additionalClass?: string;
  @Input() title?: string;
  @Input() isBlocked: boolean = false;
  @Input() isGroup: boolean = false;
  @Input() size?: string;
  @Input() isColorful: boolean = true;
  @Input() isPhotoGallery: boolean = true;
  /** Presence dot in the bottom-right corner; unknown values/empty hide it */
  @Input() status?: PresenceStatus | string | null;
  protected color?: string;
  protected hasImageError = false;

  ngOnInit() {
    if (this.mediaId) this.initAvatarUrl();
    if (this.isColorful) this.color = getColorByLetter(this.title);
  }

  private initAvatarUrl() {
    if (!this.chatId || !this.mediaId) return;

    const cacheKey = `/${this.mediaId}`;

    const fetch$ = this.avatarService.getAvatarBlob(this.chatId, this.mediaId);

    this.cacheService.getCachedOrFetchBlob(cacheKey, fetch$, CACHE_NAMES.CHAT_PROFILE_PHOTO).subscribe({
      next: (data) => {
        if (!data) return;
        this.url = URL.createObjectURL(data);
      },
    });
  }

  protected get statusClass(): string | null {
    return this.status ? (PRESENCE_STATUS_CLASSES[this.status as PresenceStatus] ?? null) : null;
  }

  onImgError() {
    this.hasImageError = true;
  }

  protected readonly DEFAULT_GROUP_ICON = DEFAULT_GROUP_ICON;
  protected readonly getInitials = getInitials;
  protected readonly DEFAULT_PROFILE_ICON = DEFAULT_PROFILE_ICON;
}
